import { api, handleApiError } from '../client'

export interface GenerationOptions {
  tone: string
  length: string
  creativity: number
  includeImages: boolean
  language: string
}

export interface GenerateContentRequest {
  prompt: string
  templateType?: string
  options: GenerationOptions
}

export interface GenerateContentResponse {
  id: string
  content: string
  tokensUsed: number
  model: string
  createdAt: string
}

export interface RecentGeneration {
  id: string
  type: string
  prompt: string
  preview: string
  tokensUsed: number
  model: string
  createdAt: string
}

export interface AIUsageStats {
  totalGenerations: number
  totalTokensUsed: number
  generationsToday: number
  tokensUsedToday: number
  favoriteModel: string
}

export const aiService = {
  /**
   * Generate content using AI.
   */
  async generateContent(request: GenerateContentRequest): Promise<GenerateContentResponse> {
    try {
      const response = await api.post<GenerateContentResponse>('/v1/ai/generate', request)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get recent AI generations.
   */
  async getRecentGenerations(limit: number = 10): Promise<RecentGeneration[]> {
    try {
      const response = await api.get<RecentGeneration[]>(`/v1/ai/generations?limit=${limit}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get a specific generation by ID.
   */
  async getGeneration(id: string): Promise<RecentGeneration> {
    try {
      const response = await api.get<RecentGeneration>(`/v1/ai/generations/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Delete a generation.
   */
  async deleteGeneration(id: string): Promise<void> {
    try {
      await api.delete(`/v1/ai/generations/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get AI usage statistics.
   */
  async getUsageStats(): Promise<AIUsageStats> {
    try {
      const response = await api.get<AIUsageStats>('/v1/ai/stats')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
